import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { butterflyBlack } from '../../commonservice';
import './PaymentPage.scss';

// Initialize Stripe (replace with your publishable key)
const stripePromise = loadStripe('pk_test_your_publishable_key_here');

interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  features: string[];
  popular?: boolean;
}

interface PaymentFormProps {
  selectedPlan: Plan;
  onPaymentSuccess: () => void;
  onPaymentError: (error: string) => void;
}

const plans: Plan[] = [
  {
    id: 'basic',
    name: 'Basic Plan',
    price: 4.99,
    period: 'month',
    features: [
      'Up to 5 pages',
      'Basic privacy controls',
      'Email support',
      'Standard templates'
    ]
  },
  {
    id: 'premium',
    name: 'Premium Plan',
    price: 9.99,
    period: 'month',
    features: [
      'Unlimited pages and posts',
      'Advanced privacy controls',
      'Priority customer support',
      'Custom branding options',
      'Analytics and insights',
      'Advanced media uploads'
    ],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise Plan',
    price: 19.99,
    period: 'month',
    features: [
      'Everything in Premium',
      'Team collaboration',
      'Advanced analytics',
      'Custom integrations',
      'Dedicated support',
      'White-label options'
    ]
  }
];

const PaymentForm: React.FC<PaymentFormProps> = ({ selectedPlan, onPaymentSuccess, onPaymentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setError('Card element not found');
      setLoading(false);
      return;
    }

    try {
      // Create payment method
      const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: billingDetails.name,
          email: billingDetails.email,
          address: {
            line1: billingDetails.address,
            city: billingDetails.city,
            state: billingDetails.state,
            postal_code: billingDetails.zipCode,
          },
        },
      });

      if (paymentMethodError) {
        setError(paymentMethodError.message || 'Payment failed');
        onPaymentError(paymentMethodError.message || 'Payment failed');
        setLoading(false);
        return;
      }

      // Here you would typically send the paymentMethod.id to your backend
      // to complete the payment with the selected plan
      console.log('Payment method created:', paymentMethod);
      console.log('Selected plan:', selectedPlan);

      // Simulate successful payment
      setTimeout(() => {
        onPaymentSuccess();
        setLoading(false);
      }, 2000);

    } catch (err) {
      setError('An unexpected error occurred');
      onPaymentError('An unexpected error occurred');
      setLoading(false);
    }
  };

  const handleCardChange = (event: any) => {
    setCardComplete(event.complete);
    if (event.error) {
      setError(event.error.message);
    } else {
      setError(null);
    }
  };

  const handleBillingChange = (field: string, value: string) => {
    setBillingDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
        iconColor: '#F8B195',
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  const isFormValid = () => {
    return cardComplete && 
           billingDetails.name && 
           billingDetails.email && 
           billingDetails.address && 
           billingDetails.city && 
           billingDetails.state && 
           billingDetails.zipCode;
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="billing-details">
        <h4>Billing Information</h4>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input
              type="text"
              value={billingDetails.name}
              onChange={(e) => handleBillingChange('name', e.target.value)}
              className="form-input"
              placeholder="John Doe"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              value={billingDetails.email}
              onChange={(e) => handleBillingChange('email', e.target.value)}
              className="form-input"
              placeholder="john@example.com"
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Address</label>
          <input
            type="text"
            value={billingDetails.address}
            onChange={(e) => handleBillingChange('address', e.target.value)}
            className="form-input"
            placeholder="123 Main St"
            required
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">City</label>
            <input
              type="text"
              value={billingDetails.city}
              onChange={(e) => handleBillingChange('city', e.target.value)}
              className="form-input"
              placeholder="New York"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">State</label>
            <input
              type="text"
              value={billingDetails.state}
              onChange={(e) => handleBillingChange('state', e.target.value)}
              className="form-input"
              placeholder="NY"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">ZIP Code</label>
            <input
              type="text"
              value={billingDetails.zipCode}
              onChange={(e) => handleBillingChange('zipCode', e.target.value)}
              className="form-input"
              placeholder="10001"
              required
            />
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Card Information</label>
        <div className="card-element-container">
          <CardElement
            options={cardElementOptions}
            onChange={handleCardChange}
            className="card-element"
          />
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="fa fa-exclamation-circle"></i>
          {error}
        </div>
      )}

      <div className="order-summary">
        <h4>Order Summary</h4>
        <div className="summary-item">
          <span>{selectedPlan.name}</span>
          <span>${selectedPlan.price}/{selectedPlan.period}</span>
        </div>
        <div className="summary-total">
          <span>Total</span>
          <span>${selectedPlan.price}/{selectedPlan.period}</span>
        </div>
      </div>

      <button
        type="submit"
        disabled={!stripe || !isFormValid() || loading}
        className="payment-button"
      >
        {loading ? (
          <>
            <i className="fa fa-spinner fa-spin"></i>
            Processing Payment...
          </>
        ) : (
          <>
            <i className="fa fa-lock"></i>
            Pay ${selectedPlan.price}/{selectedPlan.period}
          </>
        )}
      </button>
    </form>
  );
};

const PaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<Plan>(plans[1]); // Default to Premium

  const handlePaymentSuccess = () => {
    setShowSuccess(true);
    setTimeout(() => {
      navigate('/dashboard'); // Redirect to dashboard after successful payment
    }, 3000);
  };

  const handlePaymentError = (error: string) => {
    setErrorMessage(error);
    setShowError(true);
  };

  const handleBackToSignup = () => {
    navigate('/register');
  };

  return (
    <div className="payment-page">
      <div className="payment-container">
        <div className="payment-header">
          <img src={butterflyBlack} alt="Living Legacy" className="logo" />
          <h1>Complete Your Registration</h1>
          <p>Choose your plan and complete your payment to start creating your legacy</p>
        </div>

        {showSuccess ? (
          <div className="success-container">
            <div className="success-icon">
              <i className="fa fa-check-circle"></i>
            </div>
            <h2>Payment Successful!</h2>
            <p>Welcome to Living Legacy. You're all set to start creating your digital legacy.</p>
            <div className="loading-spinner">
              <i className="fa fa-spinner fa-spin"></i>
              <span>Redirecting to dashboard...</span>
            </div>
          </div>
        ) : showError ? (
          <div className="error-container">
            <div className="error-icon">
              <i className="fa fa-exclamation-triangle"></i>
            </div>
            <h2>Payment Failed</h2>
            <p>{errorMessage}</p>
            <button onClick={() => setShowError(false)} className="retry-button">
              Try Again
            </button>
          </div>
        ) : (
          <div className="payment-content">
            <div className="plan-selection">
              <h3>Choose Your Plan</h3>
              <div className="plan-options">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`plan-option ${selectedPlan.id === plan.id ? 'active' : ''} ${plan.popular ? 'popular' : ''}`}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    {plan.popular && <div className="popular-badge">Most Popular</div>}
                    <div className="plan-header">
                      <h4>{plan.name}</h4>
                      <div className="plan-price">
                        <span className="currency">$</span>
                        <span className="amount">{plan.price}</span>
                        <span className="period">/{plan.period}</span>
                      </div>
                    </div>
                    <ul className="plan-features">
                      {plan.features.map((feature, index) => (
                        <li key={index}>
                          <i className="fa fa-check"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            <div className="payment-section">
              <h3>Payment Information</h3>
              <Elements stripe={stripePromise}>
                <PaymentForm
                  selectedPlan={selectedPlan}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                />
              </Elements>
            </div>

            <div className="security-notice">
              <i className="fa fa-shield-alt"></i>
              <span>Your payment information is secure and encrypted with bank-level security</span>
            </div>

            <button onClick={handleBackToSignup} className="back-button">
              <i className="fa fa-arrow-left"></i>
              Back to Sign Up
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPage; 